/**
 * Formatter-Helfer für Stellungnahme-Platzhalter (DE-Locale).
 *
 * Pure Functions ohne Seiteneffekte. Müssen deterministisch sein, weil ihre
 * Outputs in formellen Stellungnahmen an Jobcenter landen.
 */

import type { MoneyCents } from '../schema/brands.js';

const MONTHS_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

/** „6. Mai 2026" — das in Behörden-Schreiben übliche Long-Format. */
export function formatGermanDate(date: Date): string {
  const day = date.getUTCDate();
  const month = MONTHS_DE[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${day}. ${month} ${year}`;
}

/**
 * „2.400 €" — deutsches Euro-Format ohne Cent-Anteil (gerundet).
 * In Stellungnahmen wird üblicherweise auf den vollen Euro gerundet.
 */
export function formatEuro(cents: MoneyCents): string {
  const euros = Math.round((cents as number) / 100);
  return `${euros.toLocaleString('de-DE')} €`;
}

/** „+785 €" oder „−785 €" mit explizitem Vorzeichen, für Tragfähigkeitslücke. */
export function formatEuroSigned(cents: MoneyCents): string {
  const euros = Math.round((cents as number) / 100);
  if (euros === 0) return '0 €';
  const sign = euros > 0 ? '+' : '−';
  return `${sign}${Math.abs(euros).toLocaleString('de-DE')} €`;
}

/** „38 €" für Stundensätze. */
export function formatEuroPerHour(eurosPerHour: number): string {
  return `${Math.round(eurosPerHour).toLocaleString('de-DE')} €`;
}

/** „74 %" — gerundet auf ganze Prozent. */
export function formatPercent(value: number): string {
  return `${Math.round(value)} %`;
}

/**
 * BG-Beschreibung in narrativem Text:
 * „2 Erwachsene, 2 Kinder (8 und 12 Jahre)"
 * „Alleinstehende Person"
 */
export function describeBgComposition(
  members: ReadonlyArray<{ role: string; age: number }>,
): string {
  const adults = members.filter((m) => m.role === 'antragsteller' || m.role === 'partner');
  const children = members.filter((m) => m.role === 'kind');

  if (members.length === 1 && adults.length === 1) {
    return 'Alleinstehende Person';
  }

  const parts: string[] = [];
  if (adults.length === 1) parts.push('1 Erwachsene Person');
  else if (adults.length > 1) parts.push(`${adults.length} Erwachsene`);

  if (children.length === 1 && children[0]) {
    parts.push(`1 Kind (${children[0].age} Jahre)`);
  } else if (children.length > 1) {
    const ages = children.map((c) => c.age).join(' und ');
    parts.push(`${children.length} Kinder (${ages} Jahre)`);
  }

  return parts.join(', ');
}

/** Kurzformel für Header: „Bedarfsgemeinschaft mit 4 Personen". */
export function shortBgComposition(memberCount: number): string {
  if (memberCount === 1) return 'Einpersonen-Bedarfsgemeinschaft';
  return `Bedarfsgemeinschaft mit ${memberCount} Personen`;
}
